'use client'

import { ExternalLink } from 'lucide-react'
import { useTheme } from 'next-themes'
import { useEffect, useState } from 'react'
import { codeToHtml } from 'shiki'

interface ClickableCodeBlockProps {
  code: string
  href: string
  lang?: string
  className?: string
}

export function ClickableCodeBlock({ code, href, lang = 'text', className = '' }: ClickableCodeBlockProps) {
  const { resolvedTheme } = useTheme()
  const [html, setHtml] = useState('')
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    const generateHighlightedCode = async () => {
      // Use the same themes as configured in source.config.ts
      const theme = mounted && resolvedTheme === 'dark' ? 'github-dark' : 'github-light'

      const highlightedHtml = await codeToHtml(code, {
        lang,
        theme,
      })

      setHtml(highlightedHtml)
    }

    generateHighlightedCode()
  }, [code, lang, mounted, resolvedTheme])

  // Show a simple code block during SSR and initial load
  if (!mounted || !html) {
    return (
      <a
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        className={`group block cursor-pointer focus:outline-none bg-transparent text-inherit no-underline ${className}`}>
        <figure
          dir='ltr'
          className='my-4 rounded-xl bg-fd-card p-1 shiki relative border outline-none not-prose overflow-hidden text-sm shiki shiki-themes github-light github-dark'
          tabIndex={0}>
          <div className='empty:hidden absolute top-1 right-1 z-2 bg-fd-card rounded-bl-lg border-l border-b text-fd-muted-foreground'>
            <div className='inline-flex items-center justify-center rounded-md p-2 text-sm font-medium'>
              <ExternalLink className='size-3.5 text-gray-500 group-hover:text-gray-800 dark:text-gray-400 dark:group-hover:text-white transition-colors duration-200' />
            </div>
          </div>
          <div
            className='bg-fd-secondary rounded-lg border text-[13px] py-3.5 overflow-auto max-h-[600px] fd-scroll-container'
            style={{ '--padding-right': 'calc(var(--spacing) * 8)' } as React.CSSProperties}>
            <pre className='min-w-full w-max *:flex *:flex-col'>
              <code>
                <span className='line'>
                  <span>{code}</span>
                </span>
              </code>
            </pre>
          </div>
        </figure>
      </a>
    )
  }

  return (
    <a
      href={href}
      target='_blank'
      rel='noopener noreferrer'
      className={`group block cursor-pointer focus:outline-none bg-transparent text-inherit no-underline ${className}`}>
      <figure
        dir='ltr'
        className='my-4 rounded-xl bg-fd-card p-1 shiki relative border outline-none not-prose overflow-hidden text-sm shiki shiki-themes github-light github-dark'
        tabIndex={0}>
        <div className='empty:hidden absolute top-1 right-1 z-2 bg-fd-card rounded-bl-lg border-l border-b text-fd-muted-foreground'>
          <div className='inline-flex items-center justify-center rounded-md p-2 text-sm font-medium'>
            <ExternalLink className='size-3.5 text-gray-500 group-hover:text-gray-800 dark:text-gray-400 dark:group-hover:text-white transition-colors duration-200' />
          </div>
        </div>
        <div
          className='bg-fd-secondary rounded-lg border text-[13px] py-3.5 overflow-auto max-h-[600px] fd-scroll-container'
          style={{ '--padding-right': 'calc(var(--spacing) * 8)' } as React.CSSProperties}>
          <div
            dangerouslySetInnerHTML={{
              __html: html
                .replace(/<figure[^>]*>|<\/figure>/g, '')
                .replace(/<div[^>]*class="[^"]*top-1 right-1[^"]*"[^>]*>.*?<\/div>/g, '')
                .replace(/style="[^"]*background-color:[^;]*;?[^"]*"/g, '')
                .replace(/style="[^"]*color:[^;]*;?[^"]*"/g, '')
                .replace(/style=""/g, ''),
            }}
          />
        </div>
      </figure>
    </a>
  )
}
